import React, { useCallback, useEffect, useState } from 'react';
import { Bot, Plus, Copy, Trash2, Power, KeyRound, Terminal } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export const AgentApiPanel = () => {
  const [data, setData] = useState({ keys: [], tools: [] });
  const [name, setName] = useState('');
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get('/integrations/api-keys'); setData(d); }
    catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) return null;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast({ title: `${label} copiado` }); }
    catch (e) { toast({ title: 'Não foi possível copiar' }); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data: k } = await api.post('/integrations/api-keys', { name: name || 'Agente de IA' });
      setCreated(k); setName(''); await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };
  const toggle = async (k) => { await api.put(`/integrations/api-keys/${k.id}`, { active: !k.active }); load(); };
  const remove = async (k) => {
    if (!window.confirm(`Excluir a chave "${k.name}"?\n\nOs agentes que usam essa chave perdem o acesso imediatamente.`)) return;
    await api.delete(`/integrations/api-keys/${k.id}`); toast({ title: 'Chave excluída' }); load();
  };

  const mcpUrl = `${BACKEND}/api/mcp`;
  const restUrl = `${BACKEND}/api/v1`;
  const mcpConfig = `{
  "mcpServers": {
    "speed-motors": {
      "url": "${mcpUrl}",
      "headers": { "Authorization": "Bearer SUA_API_KEY" }
    }
  }
}`;
  const curlExample = `curl -X POST ${restUrl}/tools/list_inventory \\
  -H "Authorization: Bearer SUA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"Disponível","limit":5}'`;

  return (
    <div className="ent-panel overflow-hidden" data-testid="agent-api-panel">
      <div className="ent-colhead" style={{ borderTop: '2px solid #8b5cf6' }}>
        <Bot className="w-3.5 h-3.5 text-violet-400" />
        <span className="ent-coltitle uppercase tracking-wide">API &amp; Servidor MCP para agentes de IA</span>
        <span className="ent-pill" data-testid="api-keys-count">{data.keys.length}</span>
        <span className="text-[11px] t-label ml-auto hidden sm:block">{data.tools.length} ferramentas · leitura e escrita</span>
      </div>

      <div className="p-3.5 space-y-3">
        <p className="text-[12px] text-muted-foreground">Conecte Claude, ChatGPT, n8n ou qualquer agente ao DMS. O <b className="text-foreground">servidor MCP</b> (HTTP) expõe estoque, CRM, deals F&amp;I, agenda, pós-venda, inspeções, recon e relatórios. A mesma chave funciona na <b className="text-foreground">API REST</b>.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
            <p className="ent-label mb-1">Endpoint MCP (Streamable HTTP)</p>
            <div className="flex items-center gap-2">
              <code className="text-[11.5px] truncate flex-1" data-testid="mcp-url">{mcpUrl}</code>
              <button data-testid="copy-mcp-url" onClick={() => copy(mcpUrl, 'URL do MCP')} className="text-primary hover:text-primary/80"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
            <p className="ent-label mb-1">API REST (base)</p>
            <div className="flex items-center gap-2">
              <code className="text-[11.5px] truncate flex-1" data-testid="rest-url">{restUrl}</code>
              <button data-testid="copy-rest-url" onClick={() => copy(restUrl, 'URL da API')} className="text-primary hover:text-primary/80"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="ent-label block mb-1">Nome da chave</label>
            <input data-testid="api-key-name" className="ent-input" style={{ width: 240 }} placeholder="Ex: Agente Claude, n8n, ChatGPT" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <button data-testid="api-key-create" onClick={create} disabled={busy} className="ent-btn ent-btn-primary"><Plus className="w-3.5 h-3.5" /> {busy ? 'Gerando...' : 'Gerar API key'}</button>
          <button data-testid="api-tools-toggle" onClick={() => setShowTools((s) => !s)} className="ent-btn ml-auto"><Terminal className="w-3.5 h-3.5" /> {showTools ? 'Esconder' : 'Ver'} ferramentas ({data.tools.length})</button>
        </div>

        {created && (
          <div className="rounded-md border border-[hsl(var(--warn))]/50 bg-[hsl(var(--warn))]/[0.07] p-3" data-testid="api-key-created">
            <p className="text-[12px] font-semibold t-warn mb-1.5"><KeyRound className="w-3.5 h-3.5 inline mr-1" />Copie agora — esta chave não será exibida novamente</p>
            <div className="flex items-center gap-2">
              <code className="text-[12px] break-all flex-1" data-testid="api-key-value">{created.key}</code>
              <button data-testid="api-key-copy" onClick={() => copy(created.key, 'API key')} className="ent-btn ent-btn-sm"><Copy className="w-3 h-3" /> Copiar</button>
              <button onClick={() => setCreated(null)} className="ent-btn ent-btn-sm">Ok</button>
            </div>
          </div>
        )}

        {data.keys.length > 0 && (
          <div className="overflow-x-auto">
            <table className="ent-table">
              <thead><tr><th>Nome</th><th>Chave</th><th>Criada</th><th>Último uso</th><th className="text-right">Chamadas</th><th>Status</th><th className="text-right">Ação</th></tr></thead>
              <tbody>
                {data.keys.map((k) => (
                  <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                    <td className="font-medium">{k.name}</td>
                    <td className="mono t-label">{k.prefix}…</td>
                    <td className="num">{fmt(k.created_at)}</td>
                    <td className="num">{fmt(k.last_used_at)}</td>
                    <td className="num text-right">{k.calls || 0}</td>
                    <td><span className={`ent-status ${k.active ? 't-ok' : ''}`}><span className="ent-dot" />{k.active ? 'Ativa' : 'Revogada'}</span></td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`api-key-toggle-${k.id}`} onClick={() => toggle(k)} className="ent-btn ent-btn-sm"><Power className="w-3 h-3" /> {k.active ? 'Revogar' : 'Reativar'}</button>
                        <button data-testid={`api-key-delete-${k.id}`} onClick={() => remove(k)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showTools && (
          <div className="rounded-md border border-border max-h-[320px] overflow-y-auto" data-testid="api-tools-list">
            {data.tools.map((t) => (
              <div key={t.name} className="px-3 py-2 border-b border-border/60 last:border-0">
                <p className="mono text-[12px] font-semibold text-primary">{t.name}</p>
                <p className="text-[11.5px] text-muted-foreground">{t.description}</p>
                <p className="text-[10.5px] t-label mono mt-0.5">args: {Object.keys(t.inputSchema?.properties || {}).join(', ') || '—'}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <div className="rounded-md border border-border bg-muted/15 p-3">
            <p className="ent-label mb-1.5">Conectar no Claude / ChatGPT / n8n (MCP remoto)</p>
            <pre className="text-[11px] leading-relaxed overflow-x-auto mono text-foreground/80">{mcpConfig}</pre>
            <button data-testid="copy-mcp-config" onClick={() => copy(mcpConfig, 'Config do MCP')} className="ent-btn ent-btn-sm mt-2"><Copy className="w-3 h-3" /> Copiar config</button>
          </div>
          <div className="rounded-md border border-border bg-muted/15 p-3">
            <p className="ent-label mb-1.5">Chamada REST (qualquer ferramenta)</p>
            <pre className="text-[11px] leading-relaxed overflow-x-auto mono text-foreground/80">{curlExample}</pre>
            <p className="text-[10.5px] t-label mt-2">GET {restUrl}/tools lista tudo · atalhos: /inventory, /leads, /deals, /summary</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentApiPanel;
